/**
 * 门派任务和贡献商店数据
 */

import type { PlayerState } from '../engine/Player';

export type MissionType = 'submit_items' | 'kill_enemies' | 'reach_realm';

export interface SectMission {
  id: string;
  name: string;
  desc: string;
  type: MissionType;
  /** 目标数量 */
  target: number;
  /** 物品ID（submit_items类型） */
  itemId?: string;
  itemName?: string;
  /** 境界key前缀（reach_realm类型） */
  realmPrefix?: string;
  realmName?: string;
  /** 已完成数量（运行时） */
  progress?: number;
  /** 奖励贡献度 */
  contributionReward: number;
  /** 奖励灵石 */
  stoneReward: number;
  /** 最低门派 */
  minRealm?: number; // realmIndex
}

/** 生成每日任务池 */
export function generateMissions(player: PlayerState): SectMission[] {
  const realmIdx = getRealmIndex(player);
  const missions: SectMission[] = [];

  // 上缴灵草
  const herbCount = 3 + Math.floor(Math.random() * 5);
  missions.push({
    id: `submit_herb_${Date.now()}`,
    name: '采集灵草',
    desc: `向宗门药园上缴 ${herbCount} 株灵草`,
    type: 'submit_items',
    target: herbCount,
    itemId: 'spirit_herb',
    itemName: '灵草',
    contributionReward: 5 + herbCount * 2,
    stoneReward: 10 + herbCount * 2,
  });

  // 上缴妖丹（筑基以上）
  if (realmIdx >= 1) {
    const coreCount = 1 + Math.floor(Math.random() * 3);
    missions.push({
      id: `submit_core_${Date.now()}`,
      name: '猎杀妖兽',
      desc: `向宗门上缴 ${coreCount} 颗妖丹`,
      type: 'submit_items',
      target: coreCount,
      itemId: 'monster_core',
      itemName: '妖丹',
      contributionReward: 15 + coreCount * 5,
      stoneReward: 30 + coreCount * 10,
    });
  }

  // 上缴丹药
  missions.push({
    id: `submit_pill_${Date.now()}`,
    name: '丹药供奉',
    desc: '向宗门上缴 3 颗回春丹',
    type: 'submit_items',
    target: 3,
    itemId: 'hp_pill',
    itemName: '回春丹',
    contributionReward: 15,
    stoneReward: 20,
  });

  // 击杀任务
  const killTarget = 3 + Math.floor(Math.random() * 5) + realmIdx * 2;
  missions.push({
    id: `kill_${Date.now()}`,
    name: '斩妖除魔',
    desc: `为宗门击杀 ${killTarget} 个敌人`,
    type: 'kill_enemies',
    target: killTarget,
    contributionReward: 10 + killTarget * 3,
    stoneReward: 15 + killTarget * 5,
  });

  // 修炼任务
  if (realmIdx < 8) {
    const nextRealm = REALM_NAMES[realmIdx + 1];
    missions.push({
      id: `realm_${Date.now()}`,
      name: '突破修炼',
      desc: `突破至${nextRealm}`,
      type: 'reach_realm',
      target: 1,
      realmPrefix: REALM_KEYS[realmIdx + 1],
      realmName: nextRealm,
      contributionReward: 30 + realmIdx * 10,
      stoneReward: 50 + realmIdx * 30,
    });
  }

  return missions;
}

const REALM_KEYS = ['qi_refining','foundation','golden_core','nascent_soul','divinity','void_refining','unity','mahayana','tribulation'];
const REALM_NAMES = ['炼气期','筑基期','金丹期','元婴期','化神期','炼虚期','合体期','大乘期','渡劫期'];

function getRealmIndex(p: PlayerState): number {
  return REALM_KEYS.findIndex((k) => p.realmKey.startsWith(k));
}

/** 贡献商店物品 */
export interface SectShopItem {
  id: string;
  name: string;
  desc: string;
  cost: number;
  resultItemId?: string;
  resultCount?: number;
  stoneAmount?: number;
}

/** 生成强制任务（未完成冻结修炼） */
export function generateMandatoryMission(player: PlayerState): SectMission {
  const realmIdx = getRealmIndex(player);
  const herbCount = 5 + realmIdx * 3;
  return {
    id: `mandatory_${Date.now()}`,
    name: '宗门差事',
    desc: `向宗门上缴 ${herbCount} 株灵草（未完成将无法挂机修炼！）`,
    type: 'submit_items',
    target: herbCount,
    itemId: 'spirit_herb',
    itemName: '灵草',
    contributionReward: 15 + herbCount * 2,
    stoneReward: 20 + herbCount * 3,
  };
}

export const SECT_SHOP: SectShopItem[] = [
  { id: 'sect_stones_100', name: '灵石×100', desc: '100灵石', cost: 10, stoneAmount: 100 },
  { id: 'sect_stones_500', name: '灵石×500', desc: '500灵石', cost: 40, stoneAmount: 500 },
  { id: 'sect_hp_pill', name: '回春丹×3', desc: '宗门炼制的疗伤丹药', cost: 8, resultItemId: 'hp_pill', resultCount: 3 },
  { id: 'sect_exp_pill', name: '悟道丹×1', desc: '提升修炼感悟', cost: 20, resultItemId: 'exp_pill', resultCount: 1 },
  { id: 'sect_breakthrough', name: '筑基丹×1', desc: '突破大境界辅助丹药', cost: 50, resultItemId: 'breakthrough_pill', resultCount: 1 },
  { id: 'sect_spirit_sword', name: '灵剑', desc: '宗门制式兵器', cost: 30, resultItemId: 'spirit_sword', resultCount: 1 },
  { id: 'sect_spirit_armor', name: '灵甲', desc: '宗门制式防具', cost: 30, resultItemId: 'spirit_armor', resultCount: 1 },
];
